import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Icon,
  Switch,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  FormHelperText,
  Checkbox,
} from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  getStoreCategoriesApi,
  getStoreCustomerGroupsApi,
  getStoreProductsApi,
  updateCouponRuleCombinedApi,
  updateCouponRuleIfNoCodeExists,
} from 'api';
import Autocomplete from '@material-ui/lab/Autocomplete';
import NumberFormat from 'react-number-format';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { Link } from 'react-router-dom';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import DateFnsUtils from '@date-io/date-fns';
import { BackdropCircular, InfoTooltip } from 'components';
import { format, parseISO, isValid } from 'date-fns';
import { makeStyles } from '@material-ui/core/styles';


const defaultCustomerGroups = [
  {
    id: 0,
    code: 'Not Logged In'
  }
];

const useStyles = makeStyles({
  rootControl: {
    '& .MuiFormControlLabel-root': {
      width: 'fit-content',
      whiteSpace: 'nowrap',
    },
  },
});

const EditCouponRule = ({setOpenEditRuleDialog}) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const {storeActiveCategoriesWithActiveNavLevel, loadingCategories} = useSelector((state) => state.categories);
  const {store_products: {catalogProducts}, productsLoading} = useSelector((state) => state.products);
  const { customerGroups = [], } = useSelector((state) => state.customers);
  const { editableRule = {}, loadingUpdateCouponRule } = useSelector((state) => state.coupon_rules);

  const isCreatedByMagento = editableRule.extension_attributes?.created_by === 'DermPRO';
  const isCouponExist = editableRule.coupon_type === 'SPECIFIC_COUPON';
  const allCustomerGroups = [...defaultCustomerGroups, ...customerGroups];
  const isComplex = ['buyxgetn_fixdisc', 'buyxgetn_fixprice', 'buyxgetn_perc', 'eachn_perc', 'eachn_fixdisc',  'setof_percent', 'setof_fixed'].includes(editableRule.simple_action);
  const isBasic = ['by_percent', 'eachn_fixprice', 'by_fixed', 'cart_fixed'].includes(editableRule.simple_action);
  const NthSimpleActions = ['eachn_perc', 'eachn_fixdisc', 'eachn_fixprice'];
  const buyXgetYActions = ['buyxgetn_fixdisc', 'buyxgetn_fixprice', 'buyxgetn_perc'];

  useEffect(() => {
    if (!storeActiveCategoriesWithActiveNavLevel.length) {
      getStoreCategoriesApi({dispatch});
    }

    if (!catalogProducts.length) {
      getStoreProductsApi({dispatch});
    }

    if (!customerGroups.length) {
      getStoreCustomerGroupsApi({dispatch});
    }
  }, []);

  const changeSpecificCategoriesAppliesTo = (value, action, option, values, setFieldValue) => {
    const conditions = values.action_condition.conditions.map((condition) => {
      if (condition.attribute_name === 'category_ids' && condition.operator === '()') {
        if (action === 'select-option') {
          return {
            ...condition,
            value: condition.value.length > 1 ?
              [...condition.value.split(', '), String(option.option.id)].join(', ')
              : value.map((v) => v.id).join('')
          };
        }

        if (action === 'remove-option') {
          const filteredValues = condition.value.split(', ').filter((id) => id != option.option.id);
          return {
            ...condition,
            value: filteredValues.join(', ')
          };
        }
      }

      return condition;
    });

    setFieldValue('action_condition.conditions', conditions);
  };

  const onBlurSpecificCategoriesAppliesTo = (values, setFieldTouched) => {
    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
      condition.attribute_name === 'category_ids' && condition.operator === '()'
    ));

    setFieldTouched(`action_condition.conditions[${currentIndex}]`, true);
  };

  const onClickSpecificListingsAppliesTo =  (values, setFieldValue) => {
    const specificCategories = {
      ...values.sku,
      operator: '()',
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '{}' && cond.operator !== '()'
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, specificCategories]);
  };

  const changeSpecificListingsAppliesTo =  (value, action, option, values, setFieldValue) => {
    const conditions = values.action_condition.conditions.map((condition) => {
      if (condition.attribute_name === 'sku' && condition.operator === '()') {
        if (action === 'select-option') {
          return {
            ...condition,
            value: condition.value.length > 1 ?
              [...condition.value.split(', '), String(option.option.sku)].join(', ')
              : value.map((v) => v.sku).join('')
          };
        }

        if (action === 'remove-option') {
          const filteredValues = condition.value.split(', ').filter((sku) => sku != option.option.sku);
          return {
            ...condition,
            value: filteredValues.join(', ')
          };
        }
      }

      return condition;
    });

    setFieldValue('action_condition.conditions', conditions);
  };

  const onBlurSpecificListingsAppliesTo = (values, setFieldTouched) => {
    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
      condition.attribute_name === 'sku' && condition.operator === '()'
    ));

    setFieldTouched(`action_condition.conditions[${currentIndex}]`, true);
  };

  const onClickAllProductsApplyTo = (values, setFieldValue) => {
    const allProducts = {
      ...values.sku,
      value: 'PROD',
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '{}' && cond.operator !== '()'
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, allProducts]);
  };

  const onClickAllTreatmentsApplyTo = (values, setFieldValue) => {
    const allTreatments = {
      ...values.sku,
      value: 'TREAT'
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '{}' && cond.operator !== '()'
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, allTreatments]);
  };

  const onClickSpecificCategoriesNotApplyTo = (values, setFieldValue) => {
    const specificCategories = {
      ...values.category_ids,
      operator: '!()'
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '!()'
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, specificCategories]);
  };

  const changeSpecificCategoriesNotApplyTo = (value, action, option, values, setFieldValue) => {
    const conditions = values.action_condition.conditions.map((condition) => {
      if (condition.attribute_name === 'category_ids' && condition.operator === '!()') {
        if (action === 'select-option') {
          return {
            ...condition,
            value: condition.value.length > 1 ?
              [...condition.value.split(', '), String(option.option.id)].join(', ')
              : value.map((v) => v.id).join('')
          };
        }

        if (action === 'remove-option') {
          const filteredValues = condition.value.split(', ').filter((id) => id != option.option.id);
          return {
            ...condition,
            value: filteredValues.join(', ')
          };
        }
      }

      return condition;
    });

    setFieldValue('action_condition.conditions', conditions);
  };

  const onBlurSpecificCategoriesNotApplyTo = (values, setFieldTouched) => {
    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
      condition.attribute_name === 'category_ids' && condition.operator === '!()'
    ));

    setFieldTouched(`action_condition.conditions[${currentIndex}]`, true);
  };

  const onClickSpecificListingsNotApplyTo = (values, setFieldValue) => {
    const specificCategories = {
      ...values.sku,
      operator: '!()'
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '!()'
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, specificCategories]);
  };

  const changeSpecificListingsNotApplyTo =  (value, action, option, values, setFieldValue) => {
    const conditions = values.action_condition.conditions.map((condition) => {
      if (condition.attribute_name === 'sku' && condition.operator === '!()') {
        if (action === 'select-option') {
          return {
            ...condition,
            value: condition.value.length > 1 ?
              [...condition.value.split(', '), String(option.option.sku)].join(', ')
              : value.map((v) => v.sku).join('')
          };
        }

        if (action === 'remove-option') {
          const filteredValues = condition.value.split(', ').filter((sku) => sku != option.option.sku);
          return {
            ...condition,
            value: filteredValues.join(', ')
          };
        }
      }

      return condition;
    });

    setFieldValue('action_condition.conditions', conditions);
  };

  const onBlurSpecificListingsNotApplyTo =  (values, setFieldTouched) => {
    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
      condition.attribute_name === 'sku' && condition.operator === '!()'
    ));

    setFieldTouched(`action_condition.conditions[${currentIndex}]`, true);
  };

  const getCurrentError = (values, attributeName, operator, errors, touched) => {
    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
      condition.attribute_name === attributeName && condition.operator === operator
    ));

    if (currentIndex === -1) {
      return false;
    }

    return touched.action_condition?.conditions[currentIndex] && errors.action_condition?.conditions[currentIndex]?.value;
  };

  const changeMinOrderQuontity = (value, values, setFieldValue) => {
    const productsQuontityParams = {
      ...values.quote_item_qty,
      value: value.value
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '>='
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, productsQuontityParams]);
  };

  const changePurchaseAmount = (value, values, setFieldValue) => {
    const purchaseAmount = {
      ...values.quote_item_row_total,
      value: value.value
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '>='
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, purchaseAmount]);
  };

  const changeSpecificCustomersGroup = (event, value, action, option, values, setFieldValue) => {
    event.preventDefault();

    if (action === 'select-option') {
      setFieldValue(
        'customer_group_ids',
        value !== null ?
          [...values.customer_group_ids, option.option.id]
          : values.customer_group_ids
      );
    }

    if (action === 'remove-option') {
      setFieldValue(
        'customer_group_ids',
        value !== null ?
          values.customer_group_ids.filter((id) => id !== option.option.id)
          : values.customer_group_ids
      );
    }
  };

  const findProducts = (values, operator) => {
    let seectedSkus;

    if (operator === 'promo_skus') {
      seectedSkus = values.promo_skus.split(', ');
    } else {
      seectedSkus = values.action_condition.conditions.find((cond) => (
        cond.attribute_name === 'sku' && cond.operator === operator
      ))?.value.split(', ') || [];
    }

    const selectedProducts = catalogProducts.filter((product) => seectedSkus.includes(product.sku)) || '';

    return selectedProducts;
  };

  const findCategories = (values, operator) => {
    const selectedCategoriesIds = values.action_condition.conditions.reduce(
      (result, cond) => {
        if (cond.attribute_name === 'category_ids' && cond.operator === operator) {
          const categoryIds = cond.value.split(', ').map(Number);
          return [...result, ...categoryIds];
        }

        return result;
      },
      []
    );
    const selectedCategories = storeActiveCategoriesWithActiveNavLevel.filter((category) => {
      return selectedCategoriesIds.includes(category.id);
    });

    return selectedCategories;
  };

  const changeSetOfSKU = (value, action, option, values, setFieldValue) => {
    const {promo_skus} = values;

    let selectedSKUs = promo_skus.length ? promo_skus.split(', ') : [];

    if (action === 'select-option') {
      selectedSKUs.push(option.option.sku);
    }

    if (action === 'remove-option') {
      selectedSKUs = selectedSKUs.filter((sku) => sku !== option.option.sku);
    }

    setFieldValue('promo_skus', selectedSKUs.join(', '));
  };

  const renderOption = (option, { inputValue, type }) => {
    const optionText = type === 'categories' ? option?.nameWithPath : option.name;
    const matches = match(optionText, inputValue);
    const parts = parse(optionText, matches);

    return (
      <div>
        {parts.map((part, index) => (
          <span key={index} className={part.highlight ? 'text-primary font-bold' : ''}>
            {part.text}
          </span>
        ))}
        {type === 'listings' && Boolean(option.price) && (
          <span className="ml-2 text-green">${option.price}</span>
        )}
      </div>
    );
  };

  const getDefineYProductOptions = (values) => {
    const selectedSKUs = values.action_condition.conditions.find((condition) => condition.attribute_name === 'sku' && condition.operator === '()')?.value.split(', ');

    const options = catalogProducts.filter((product) => !selectedSKUs?.includes(product.sku));
    return options;
  };

  const getDefineXProductOptions = (values) => {
    const selectedSKUs = values.promo_skus ? values.promo_skus.split(', ') : [];

    const options = catalogProducts.filter((product) => !selectedSKUs?.includes(product.sku));
    return options;
  };

  return (
    <div className="pt-10 pl-6">
      {customerGroups.length !== 0 && !!Object.keys(editableRule).length &&
      <Formik
        initialValues={{
          rule_id: editableRule.rule_id,
          is_active: editableRule.is_active,
          name: editableRule.name,
          description: editableRule.description || '',
          coupon_code: editableRule.coupon_code || {
            code: '',
            is_primary: true,
            type: 0
          },
          uses_per_coupon: editableRule.uses_per_coupon || 0,
          uses_per_customer: editableRule.uses_per_customer || 0,
          discount_amount: editableRule.discount_amount || 0,
          discount_step: editableRule.discount_step,
          simple_action: editableRule.simple_action,
          minOrder: editableRule.action_condition?.conditions?.find((con) => con.operator === '>=')?.attribute_name || '',
          simple_free_shipping: editableRule.simple_free_shipping || '0',
          discount_qty: editableRule.discount_qty || '',
          customer_group_ids: editableRule.customer_group_ids || [],
          website_ids: editableRule.website_ids,
          from_date: editableRule.from_date || '',
          to_date: editableRule.to_date || '',
          allowEndDate: editableRule.to_date,
          sort_order: editableRule.sort_order,
          created_by: editableRule.extension_attributes?.created_by || '',
          action_condition: {
            ...(editableRule.action_condition || {}),
            conditions: editableRule.action_condition?.conditions || []
          },
          promo_skus: editableRule.extension_attributes?.amrules?.promo_skus || '',
          nqty: editableRule.extension_attributes?.amrules?.nqty || '',
          max_discount: editableRule.extension_attributes?.amrules?.max_discount || '',
          coupon_type: editableRule.coupon_type,
          use_auto_generation: editableRule.use_auto_generation,
          sku: {
            attribute_name: 'sku',
            operator: '{}',
            value: '',
            condition_type: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product'
          },
          category_ids: {
            attribute_name: 'category_ids',
            operator: '()',
            value: '',
            condition_type: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product'
          },
          quote_item_row_total: {
            attribute_name: 'quote_item_row_total',
            operator: '>=',
            value: '',
            condition_type: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product'
          },
          quote_item_qty: {
            attribute_name: 'quote_item_qty',
            operator: '>=',
            value: '',
            condition_type: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product'
          },
        }}
        enableReinitialize
        validationSchema={
          yup.object({
            name: yup
              .string()
              .required('Field is required'),
            coupon_code: yup
              .object()
              .shape({
                code: yup
                  .string()
              }),
            uses_per_coupon: yup
              .number()
              .integer('Only integer numbers are allowed')
              .min(0, 'Only positive numbers are allowed'),
            discount_amount: yup
              .number()
              .notOneOf([0], 'Field is required')
              .when('simple_action', {
                is: (val) => val === 'eachn_perc',
                then: yup
                  .number()
                  .max(100, 'Invalid percent')
                  .required('Field is required')
              })
              .when('simple_action', {
                is: (val) => val === 'buy_x_get_y',
                then: yup
                  .number()
                  .notOneOf([0], 'Field is required')
                  .required('Field is required')
              })
              .required('Field is required'),
            discount_step: yup
              .number()
              .when('simple_action', {
                is: (val) => val === 'buy_x_get_y',
                then: yup
                  .number()
                  .min(1, 'Field is required')
                  .required('Field is required')
              }),
            customer_group_ids: yup
              .array()
              .min(1, 'Choose a customer group')
              .required('Field is required'),
            action_condition: yup
              .object()
              .shape({
                conditions: yup
                  .array()
                  .of(
                    yup.object().shape({
                      attribute_name: yup
                        .string()
                        .required('Field is required'),
                      operator: yup
                        .string()
                        .required('Field is required'),
                      value: yup
                        .string()
                        .required('Field is required')
                    }))
              }),
            from_date: yup
              .string()
              .nullable()
              .test(
                'from_date',
                'Start Date must be earlier than End Date',
                function (value) {
                  const { to_date } = this.parent;

                  if (!value || !to_date) {
                    return true;
                  }

                  return new Date(value).setHours(0, 0, 0, 0) <= new Date(to_date).setHours(0, 0, 0, 0);
                }
              ),
            to_date: yup
              .string()
              .nullable()
              .test(
                'to_date',
                'Start Date must be earlier than End Date',
                function (value) {
                  const { from_date } = this.parent;

                  if (!value || !from_date) {
                    return true;
                  }

                  return new Date(value).setHours(0, 0, 0, 0) >= new Date(from_date).setHours(0, 0, 0, 0);
                }
              ),
          })
        }
        onSubmit={async (values) => {
          const {sku, category_ids, quote_item_row_total, quote_item_qty, minOrder, max_discount, promo_skus, nqty, allowEndDate, from_date, to_date, created_by, ...rest} = values;

          const couponRuleWithCode = {
            ...rest,
            from_date,
            to_date,
            extension_attributes: {
              created_by,
              amrules: {
                ...(max_discount && { max_discount }),
                ...(promo_skus && { promo_skus }),
                ...(nqty && { nqty }),
              },
            },
          };

          let isRuleUpdated;

          if (isCouponExist) {
            isRuleUpdated = await updateCouponRuleCombinedApi({dispatch, couponRuleWithCode});
          } else {
            isRuleUpdated = await updateCouponRuleIfNoCodeExists({dispatch, couponRuleWithCode});
          }

          if (isRuleUpdated) {
            setOpenEditRuleDialog(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched
        }) => {
          const selectedCustomerGroups = allCustomerGroups.filter((group) => values.customer_group_ids.includes(group.id)) || '';
          return (
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                {isCreatedByMagento &&
                <Typography variant="h5" className="mb-5 text-muted">
                  This sales rule can only be modified within Magento
                </Typography>
                }
                <Grid item xs={12}>
                  <Typography variant="h5">
                    General:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Is Active
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <Switch
                    color="primary"
                    name="is_active"
                    checked={values.is_active}
                    onChange={(event) => setFieldValue('is_active', event.target.checked)}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Rule Name <span className="text-error">*</span>
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    label="Name"
                    name="name"
                    className="max-w-600"
                    value={values.name}
                    onChange={(event) => {
                      handleChange(event);
                      setFieldValue('name', event.target.value);
                    }}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Description
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    multiline
                    fullWidth
                    rows={2}
                    variant="outlined"
                    label="Description"
                    name="description"
                    className="max-w-600"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{
                      className: 'p-0'
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Coupon Code
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={values.coupon_code.code}
                    name="coupon_code"
                    className="max-w-600"
                    onChange={(event) => {
                      handleChange(event);
                      setFieldValue('coupon_code', {...values.coupon_code, code: event.target.value});
                    }}
                    onBlur={handleBlur}
                    error={touched.coupon_code && Boolean(errors.coupon_code?.code)}
                    helperText={touched.coupon_code && errors.coupon_code?.code}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h5">
                    Coupon Usage:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Uses per Coupon
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <NumberFormat
                    customInput={TextField}
                    fullWidth
                    allowNegative={false}
                    decimalScale={0}
                    size="small"
                    variant="outlined"
                    className="max-w-600"
                    name="uses_per_coupon"
                    value={values.uses_per_coupon}
                    onValueChange={(value) => {
                      setFieldValue('uses_per_coupon', value.floatValue);
                    }}
                    error={touched.uses_per_coupon && Boolean(errors.uses_per_coupon)}
                    helperText={touched.uses_per_coupon && errors.uses_per_coupon}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Uses per Customer
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <NumberFormat
                    customInput={TextField}
                    fullWidth
                    allowNegative={false}
                    decimalScale={0}
                    size="small"
                    variant="outlined"
                    name="uses_per_customer"
                    className="max-w-600"
                    value={values.uses_per_customer}
                    onValueChange={(value) => {
                      setFieldValue('uses_per_customer', value.floatValue);
                    }}
                    error={touched.uses_per_customer && Boolean(errors.uses_per_customer)}
                    helperText={touched.uses_per_customer && errors.uses_per_customer}
                  />
                </Grid>
                {isBasic &&
                <>
                  <Grid item xs={12} className="block">
                    <Typography variant="h5">
                      Discount Type:
                    </Typography>
                  </Grid>
                  <FormControl component="div" fullWidth className={classes.rootControl}>
                    <RadioGroup
                      name="simple_action"
                      value={values.simple_action}
                      onChange={(event) => {
                        setFieldValue('simple_action', event.target.value);
                      }}
                    >
                      <FormControlLabel
                        value="by_percent"
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            onClick={() => {
                              setFieldValue('discount_amount', '');
                              setFieldTouched('discount_amount', false);
                            }}
                          />}
                        label="Percentage OFF"
                      />
                      {values.simple_action === 'by_percent' &&
                        <Grid container spacing={2} className="m-1">
                          <Grid item xs={12} sm={4} md={3} className="flex items-center">
                            Discount Amount <span className="text-error">*</span>
                          </Grid>
                          <Grid item xs={12} sm={8} md={9}>
                            <NumberFormat
                              customInput={TextField}
                              suffix={'%'}
                              required
                              fullWidth
                              allowNegative={false}
                              className="max-w-600"
                              size="small"
                              label="Discount"
                              variant="outlined"
                              name="discount_amount"
                              value={values.discount_amount}
                              onValueChange={(value) => {
                                setFieldValue('discount_amount', value.floatValue);
                              }}
                              onBlur={(event) => {
                                handleBlur(event);
                                setFieldValue('discount_amount', +event.target.value.replace('%', ''));
                              }}
                              error={touched.discount_amount && Boolean(errors.discount_amount)}
                              helperText={touched.discount_amount && errors.discount_amount}
                            />
                          </Grid>
                        </Grid>
                      }
                      <FormControlLabel
                        value="by_fixed"
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            onClick={() => {
                              setFieldValue('discount_amount', '');
                              setFieldTouched('discount_amount', false);
                            }}
                            checked={values.simple_action === 'by_fixed' || values.simple_action === 'cart_fixed'}
                          />}
                        label="Dollar OFF"
                      />
                      {(values.simple_action === 'by_fixed' || values.simple_action === 'cart_fixed') &&
                        <Grid container spacing={2} className="m-1">
                          <Grid item xs={12} sm={4} md={3} className="mt-3">
                            Discount Amount <span className="text-error">*</span>
                          </Grid>
                          <Grid item xs={12} sm={8} md={9}>
                            <div>
                              <NumberFormat
                                customInput={TextField}
                                prefix={'$'}
                                required
                                fullWidth
                                allowNegative={false}
                                size="small"
                                label="Discount"
                                className="max-w-600"
                                variant="outlined"
                                thousandsGroupStyle="thousand"
                                thousandSeparator
                                name="discount_amount"
                                value={values.discount_amount}
                                onValueChange={(value) => {
                                  setFieldValue('discount_amount', value.floatValue);
                                }}
                                onBlur={(event) => {
                                  handleBlur(event);
                                  setFieldValue('discount_amount', +event.target.value.replace('$', ''));
                                }}
                                error={touched.discount_amount && Boolean(errors.discount_amount)}
                                helperText={touched.discount_amount && errors.discount_amount}
                              />
                            </div>
                            <div>
                              <FormControlLabel
                                value="by_fixed"
                                control={
                                  <Radio
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      setFieldTouched('discount_amount', false);
                                    }}
                                  />}
                                label="Each Item"
                              />
                              <FormControlLabel
                                value="cart_fixed"
                                control={
                                  <Radio
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      setFieldTouched('discount_amount', false);
                                    }}
                                  />}
                                label="Whole Cart"
                              />
                            </div>
                          </Grid>
                        </Grid>
                      }
                      <FormControlLabel
                        value="eachn_fixprice"
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            onClick={() => {
                              setFieldValue('discount_amount', '');
                              setFieldTouched('discount_amount', false);
                            }}
                          />}
                        label="Fixed Price"
                      />
                      {values.simple_action === 'eachn_fixprice' &&
                        <Grid container spacing={2} className="m-1">
                          <Grid item xs={12} sm={4} md={3} className="mt-3">
                            Discount Amount <span className="text-error">*</span>
                          </Grid>
                          <Grid item xs={12} sm={8} md={9}>
                            <NumberFormat
                              customInput={TextField}
                              prefix={'$'}
                              required
                              allowNegative={false}
                              className="max-w-600"
                              fullWidth
                              size="small"
                              label="Price"
                              variant="outlined"
                              thousandsGroupStyle="thousand"
                              thousandSeparator
                              name="discount_amount"
                              value={values.discount_amount}
                              onValueChange={(value) => {
                                setFieldValue('discount_amount', value.floatValue);
                              }}
                              error={touched.discount_amount && Boolean(errors.discount_amount)}
                              helperText={touched.discount_amount && errors.discount_amount}
                            />
                          </Grid>
                        </Grid>
                      }
                    </RadioGroup>
                  </FormControl>
                  <Grid container spacing={2} className="m-1">
                    <Grid item xs={12} sm={4} md={3} className="mt-3">
                      Maximum quantity allowed to receive the discount
                    </Grid>
                    <Grid item xs={12} sm={8} md={9}>
                      <NumberFormat
                        customInput={TextField}
                        fullWidth
                        className="max-w-600 mt-2"
                        allowNegative={false}
                        size="small"
                        variant="outlined"
                        name="discount_qty"
                        value={values.discount_qty}
                        onValueChange={(value) => {
                          setFieldValue('discount_qty', value.floatValue);
                        }}
                        onBlur={(event) => {
                          handleBlur(event);
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
                }
                {isComplex &&
                  <>
                    <Typography variant="h5" className="font-bold mb-5">
                      Discount Type:
                    </Typography>
                    <FormControl component="div" className={`${classes.rootControl} w-full`}>
                      <RadioGroup
                        name="simple_action"
                        value={values.simple_action}
                        onChange={(event) => {
                          setFieldValue('simple_action', event.target.value);
                        }}
                      >
                        <FormControlLabel
                          value="buyxgetn_perc"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={buyXgetYActions.includes(values.simple_action)}
                              onClick={() => {
                                setFieldValue('discount_amount', '');
                                setFieldValue('max_discount', '');
                                setFieldValue('nqty', '');
                                setFieldValue('discount_step', '');
                                setFieldValue('promo_skus', '');
                                setFieldTouched('discount_amount', false);
                                setFieldTouched('discount_step', false);
                                setFieldValue('simple_action', 'buyxgetn_perc');
                              }}
                            />}
                          label="Buy X Get Y"
                        />
                        {buyXgetYActions.includes(values.simple_action) &&
                        <>
                          <Grid container spacing={2} className="m-1">
                            <Grid item xs={12} sm={4} md={3} className="mt-3">
                              Discount Amount <span className="text-error">*</span>
                            </Grid>
                            <Grid item xs={12} sm={8} md={9}>
                              <div>
                                <NumberFormat
                                  customInput={TextField}
                                  fullWidth
                                  required
                                  allowNegative={false}
                                  size="small"
                                  className="max-w-600"
                                  variant="outlined"
                                  name="discount_amount"
                                  value={values.discount_amount}
                                  onValueChange={(value) => {
                                    setFieldValue('discount_amount', value.floatValue);
                                  }}
                                  onBlur={() => setFieldTouched('discount_amount', true)}
                                  error={touched.discount_amount && Boolean(errors.discount_amount)}
                                  helperText={touched.discount_amount && errors.discount_amount}
                                />
                                <InfoTooltip title="This is the quantity the customer will receive for free" />
                              </div>
                              <div>
                                <FormControlLabel
                                  value="buyxgetn_perc"
                                  control={
                                    <Radio
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        setFieldValue('simple_action', 'buyxgetn_perc');
                                      }}
                                    />}
                                  label="Percent Discount"
                                />
                                <FormControlLabel
                                  value="buyxgetn_fixdisc"
                                  control={
                                    <Radio
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        setFieldValue('simple_action', 'buyxgetn_fixdisc');
                                      }}
                                    />}
                                  label="Fixed Discount"
                                />
                                <FormControlLabel
                                  value="buyxgetn_fixprice"
                                  control={
                                    <Radio
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        setFieldValue('simple_action', 'buyxgetn_fixprice');
                                      }}
                                    />}
                                  label="Fixed Price"
                                />
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={4} md={3} className="flex items-center">
                              Number of X Products <span className="text-error">*</span>
                            </Grid>
                            <Grid item xs={12} sm={8} md={9}>
                              <NumberFormat
                                customInput={TextField}
                                fullWidth
                                required
                                allowNegative={false}
                                className="max-w-600"
                                size="small"
                                variant="outlined"
                                name="discount_step"
                                value={values.discount_step}
                                onValueChange={(value) => {
                                  setFieldValue('discount_step', value.floatValue);
                                }}
                                onBlur={() => setFieldTouched('discount_step', true)}
                                error={touched.discount_step && Boolean(errors.discount_step)}
                                helperText={touched.discount_step && errors.discount_step}
                              />
                              <InfoTooltip title="For the rule 'Buy 2X, get 5Y' it is 2" />
                            </Grid>
                            <Grid item xs={12} sm={4} md={3} className="flex items-center">
                              Number of Y Products <span className="text-error">*</span>
                            </Grid>
                            <Grid item xs={12} sm={8} md={9}>
                              <NumberFormat
                                customInput={TextField}
                                fullWidth
                                required
                                allowNegative={false}
                                size="small"
                                className="max-w-600"
                                variant="outlined"
                                name="nqty"
                                value={values.nqty}
                                onValueChange={(value) => {
                                  setFieldValue('nqty', value.floatValue);
                                }}
                                error={touched.nqty && Boolean(errors.nqty)}
                                helperText={touched.nqty && errors.nqty}
                              />
                              <InfoTooltip title="For the rule 'Buy 2X, get 5Y' it is 5" />
                            </Grid>
                            <Grid item xs={12} sm={4} md={3} className="flex items-center">
                              Maximum Amount of Discount <span className="text-error">*</span>
                            </Grid>
                            <Grid item xs={12} sm={8} md={9}>
                              <NumberFormat
                                customInput={TextField}
                                fullWidth
                                allowNegative={false}
                                size="small"
                                variant="outlined"
                                className="max-w-600"
                                name="discount_amount"
                                value={values.max_discount}
                                onValueChange={(value) => {
                                  setFieldValue('max_discount', value.floatValue);
                                }}
                                onBlur={(event) => {
                                  handleBlur(event);
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4} md={3} className="flex items-center">
                              Define X Product
                            </Grid>
                            <Grid item xs={12} sm={8} md={9}>
                              {productsLoading ?
                                <div className="my-5">
                                  <span className="text-muted">Preparing Listings Data...</span>
                                  <LinearProgress />
                                </div>
                                :
                                <Autocomplete
                                  multiple
                                  fullWidth
                                  disableClearable
                                  noOptionsText={
                                    <Link
                                      to="/request-sku" target="_blank" onMouseDown={() => {
                                        window.open('/request-sku', '_blank');
                                      }}
                                    >
                                      Request Listings
                                    </Link>
                                  }
                                  size="small"
                                  className="my-5 mr-4 max-w-600"
                                  defaultValue={findProducts(values, '()')}
                                  options={getDefineXProductOptions(values)}
                                  getOptionLabel={(option) => option.name}
                                  onChange={(_, value, action, option) => changeSpecificListingsAppliesTo(value, action, option, values, setFieldValue)}
                                  onBlur={() => {
                                    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
                                      condition.attribute_name === 'sku' && condition.operator === '()'
                                    ));
                                    setFieldTouched(`action_condition.conditions[${currentIndex}]`, true);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      label="Listings"
                                      required
                                      error={Boolean(getCurrentError(values, 'sku', '()', errors, touched))}
                                      helperText={getCurrentError(values, 'sku', '()', errors, touched)}
                                    />
                                  )}
                                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                                />}
                            </Grid>
                            <Grid item xs={12} sm={4} md={3} className="flex items-center">
                              Define Y Product
                            </Grid>
                            <Grid item xs={12} sm={8} md={9}>
                              {productsLoading ?
                                <div className="my-5">
                                  <span className="text-muted">Preparing Listings Data...</span>
                                  <LinearProgress />
                                </div>
                                :
                                <Autocomplete
                                  multiple
                                  fullWidth
                                  disableClearable
                                  noOptionsText={
                                    <Link
                                      to="/request-sku" target="_blank" onMouseDown={() => {
                                        window.open('/request-sku', '_blank');
                                      }}
                                    >
                                      Request Listings
                                    </Link>
                                  }
                                  size="small"
                                  className="max-w-600"
                                  defaultValue={findProducts(values, 'promo_skus')}
                                  options={getDefineYProductOptions(values)}
                                  getOptionLabel={(option) => option.name}
                                  onChange={(_, value, action, option) => changeSetOfSKU(value, action, option, values, setFieldValue)}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      label="Listings"
                                      required
                                      error={touched.promo_skus && Boolean(errors.promo_skus)}
                                      helperText={touched.promo_skus && errors.promo_skus}
                                    />
                                  )}
                                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                                />}
                            </Grid>
                          </Grid>
                        </>
                        }
                        <FormControlLabel
                          value="eachn_perc"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={NthSimpleActions.includes(values.simple_action)}
                              onClick={() => {
                                setFieldValue('discount_amount', '');
                                setFieldValue('max_discount', '');
                                setFieldValue('nqty', '');
                                setFieldValue('promo_skus', '');
                                setFieldValue('discount_step', '');
                                setFieldValue('simple_action', 'eachn_perc');
                                setFieldTouched('discount_amount', false);
                              }}
                            />}
                          label="Every Nth"
                        />
                        {NthSimpleActions.includes(values.simple_action) &&
                          <>
                            <Grid container spacing={2} className="m-1">
                              <Grid item xs={12} sm={4} md={3} className="mt-3">
                                Discount Amount <span className="text-error">*</span>
                              </Grid>
                              <Grid item xs={12} sm={8} md={9}>
                                <div>
                                  <NumberFormat
                                    customInput={TextField}
                                    fullWidth
                                    required
                                    allowNegative={false}
                                    size="small"
                                    variant="outlined"
                                    className="max-w-600"
                                    name="discount_amount"
                                    value={values.discount_amount}
                                    onValueChange={(value) => {
                                      setFieldValue('discount_amount', value.floatValue);
                                    }}
                                    onBlur={(event) => {
                                      handleBlur(event);
                                    }}
                                    error={touched.discount_amount && Boolean(errors.discount_amount)}
                                    helperText={touched.discount_amount && errors.discount_amount}
                                  />
                                </div>
                                <div>
                                  <FormControlLabel
                                    value="eachn_perc"
                                    control={
                                      <Radio
                                        size="small"
                                        color="primary"
                                        onClick={() => {
                                          setFieldValue('simple_action', 'eachn_perc');
                                        }}
                                      />}
                                    label="Percent Discount"
                                  />
                                  <FormControlLabel
                                    value="eachn_fixdisc"
                                    control={
                                      <Radio
                                        size="small"
                                        color="primary"
                                        onClick={() => {
                                          setFieldValue('simple_action', 'eachn_fixdisc');
                                        }}
                                      />}
                                    label="Fixed Discount"
                                  />
                                  <FormControlLabel
                                    value="eachn_fixprice"
                                    control={
                                      <Radio
                                        size="small"
                                        color="primary"
                                        onClick={() => {
                                          setFieldValue('simple_action', 'eachn_fixprice');
                                        }}
                                      />}
                                    label="Fixed Price"
                                  />
                                </div>
                              </Grid>
                              <Grid item xs={12} sm={4} md={3} className="flex items-center">
                                Every Nth <span className="text-error">*</span>
                              </Grid>
                              <Grid item xs={12} sm={8} md={9}>
                                <NumberFormat
                                  customInput={TextField}
                                  fullWidth
                                  required
                                  allowNegative={false}
                                  size="small"
                                  className="max-w-600"
                                  variant="outlined"
                                  name="discount_amount"
                                  value={values.discount_step}
                                  onValueChange={(value) => {
                                    setFieldValue('discount_step', value.floatValue);
                                  }}
                                  onBlur={(event) => {
                                    handleBlur(event);
                                  }}
                                  error={touched.discount_step && Boolean(errors.discount_step)}
                                  helperText={touched.discount_step && errors.discount_step}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4} md={3} className="flex items-center">
                                Maximum Amount of Discount <span className="text-error">*</span>
                              </Grid>
                              <Grid item xs={12} sm={8} md={9}>
                                <NumberFormat
                                  customInput={TextField}
                                  fullWidth
                                  required
                                  allowNegative={false}
                                  size="small"
                                  className="max-w-600"
                                  variant="outlined"
                                  name="discount_amount"
                                  value={values.max_discount}
                                  onValueChange={(value) => {
                                    setFieldValue('max_discount', value.floatValue);
                                  }}
                                  onBlur={(event) => {
                                    handleBlur(event);
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </>
                        }
                        <FormControlLabel
                          value="setof_percent"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={['setof_percent', 'setof_fixed'].includes(values.simple_action)}
                              onClick={() => {
                                setFieldTouched('discount_amount', false);
                                setFieldValue('discount_amount', '');
                                setFieldValue('max_discount', '');
                                setFieldValue('nqty', '');
                                setFieldValue('discount_step', '');
                                setFieldValue('action_condition.conditions', []);
                              }}
                            />}
                          label="Bundle"
                        />
                        {['setof_percent', 'setof_fixed'].includes(values.simple_action) &&
                          <Grid container spacing={2} className="m-1">
                            <Grid item xs={12} sm={4} md={3} className="mt-3">
                              Discount Amount <span className="text-error">*</span>
                            </Grid>
                            <Grid item xs={12} sm={8} md={9}>
                              <div>
                                <NumberFormat
                                  customInput={TextField}
                                  required
                                  fullWidth
                                  allowNegative={false}
                                  size="small"
                                  variant="outlined"
                                  className="max-w-600"
                                  name="discount_amount"
                                  thousandsGroupStyle="thousand"
                                  thousandSeparator
                                  value={values.discount_amount}
                                  onValueChange={(value) => {
                                    setFieldValue('discount_amount', value.floatValue);
                                  }}
                                  onBlur={(event) => {
                                    handleBlur(event);
                                  }}
                                  error={touched.discount_amount && Boolean(errors.discount_amount)}
                                  helperText={touched.discount_amount && errors.discount_amount}
                                />
                              </div>
                              <div>
                                <FormControlLabel
                                  value="setof_percent"
                                  control={
                                    <Radio
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        setFieldValue('simple_action', 'setof_percent');
                                      }}
                                    />}
                                  label="Percent Discount"
                                />
                                <FormControlLabel
                                  value="setof_fixed"
                                  control={
                                    <Radio
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        setFieldValue('simple_action', 'setof_fixed');
                                      }}
                                    />}
                                  label="Fixed Price for Product Set"
                                />
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={4} md={3} className="mt-3">
                              Maximum Amount of Discount <span className="text-error">*</span>
                            </Grid>
                            <Grid item xs={12} sm={8} md={9}>
                              <NumberFormat
                                customInput={TextField}
                                fullWidth
                                required
                                allowNegative={false}
                                size="small"
                                className="max-w-600"
                                variant="outlined"
                                name="discount_amount"
                                value={values.max_discount}
                                onValueChange={(value) => {
                                  setFieldValue('max_discount', value.floatValue);
                                }}
                                onBlur={(event) => {
                                  handleBlur(event);
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4} md={3} className="flex items-center">
                              Request Listings
                            </Grid>
                            <Grid item xs={12} sm={8} md={9}>
                              {productsLoading ?
                                <div className="my-5">
                                  <span className="text-muted">Preparing Listings Data...</span>
                                  <LinearProgress />
                                </div>
                                :
                                <Autocomplete
                                  multiple
                                  fullWidth
                                  disableClearable
                                  noOptionsText={
                                    <Link
                                      to="/request-sku" target="_blank" onMouseDown={() => {
                                        window.open('/request-sku', '_blank');
                                      }}
                                    >
                                      Request Listings
                                    </Link>
                                  }
                                  size="small"
                                  className="my-5 max-w-600"
                                  defaultValue={findProducts(values, 'promo_skus')}
                                  options={catalogProducts}
                                  getOptionLabel={(option) => option.name}
                                  onChange={(_, value, action, option) => changeSetOfSKU(value, action, option, values, setFieldValue)}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      label="Listings"
                                      required
                                      error={touched.promo_skus && Boolean(errors.promo_skus)}
                                      helperText={touched.promo_skus && errors.promo_skus}
                                    />
                                  )}
                                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                                />}
                            </Grid>
                          </Grid>
                        }

                        {touched.simple_action && Boolean(errors.simple_action) &&
                        <FormHelperText>
                          <Typography color="error" variant="caption">
                            {errors.simple_action}
                          </Typography>
                        </FormHelperText>
                        }
                      </RadioGroup>
                    </FormControl>
                  </>
                }
                <FormControl className={classes.rootControl}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.simple_free_shipping == '2'}
                        name="simple_free_shipping"
                        color="primary"
                        className="mt-2"
                        onChange={(event) => setFieldValue('simple_free_shipping', event.target.checked ? '2' : '0')}
                      />}
                    label="Free Shipping"
                  />
                </FormControl>
                {!['setof_percent', 'setof_fixed'].includes(values.simple_action) &&
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h5">
                        Applies To:
                      </Typography>
                    </Grid>
                    <FormControl component="fieldset" className={`${classes.rootControl} w-full`}>
                      <RadioGroup
                        aria-label="applies to"
                      >
                        <FormControlLabel
                          value="Storewide"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!values.action_condition.conditions.some((cond) => cond.operator === '()' || cond.operator === '{}')}
                              onClick={() => {
                                const filteredConditions = values.action_condition.conditions.filter((cond) => cond.operator !== '()' && cond.operator !== '{}');
                                setFieldValue('action_condition.conditions', filteredConditions);
                              }}
                            />
                          }
                          label="Storewide"
                        />
                        <FormControlLabel
                          value="All Products"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'sku' && cond.operator === '{}' && cond.value === 'PROD'
                              )) || false}
                              onClick={() => (onClickAllProductsApplyTo(values, setFieldValue))}
                            />
                          }
                          label="All Products"
                        />
                        <FormControlLabel
                          value="All Treatments"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'sku' && cond.operator === '{}' && cond.value === 'TREAT'
                              )) || false}
                              onClick={() => onClickAllTreatmentsApplyTo(values, setFieldValue)}
                            />
                          }
                          label="All Treatments"
                        />
                        <FormControlLabel
                          value="Specific Categories"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'category_ids' && cond.operator === '()'
                              )) || false}
                              onClick={() => {
                                const filteredConditions = values.action_condition.conditions.filter((cond) => (
                                  cond.operator !== '{}' && cond.operator !== '()'
                                ));
                                setFieldValue('action_condition.conditions', [...filteredConditions, values.category_ids]);
                              }}
                            />
                          }
                          label="Specific Categories"
                        />
                        {values.action_condition.conditions.find((cond) => (
                          cond.attribute_name === 'category_ids' && cond.operator === '()'
                        )) &&
                        <>
                          {loadingCategories ?
                            <div className="my-5">
                              <span className="text-muted">Preparing Categories Data...</span>
                              <LinearProgress />
                            </div>
                            :
                            <Autocomplete
                              multiple
                              fullWidth
                              disableClearable
                              size="small"
                              className="my-5 max-w-600"
                              name="action_condition.conditions"
                              defaultValue={findCategories(values, '()')}
                              options={storeActiveCategoriesWithActiveNavLevel}
                              getOptionLabel={(option) => option.nameWithPath}
                              onChange={(_, value, action, option) => changeSpecificCategoriesAppliesTo(value, action, option, values, setFieldValue)}
                              onBlur={() => onBlurSpecificCategoriesAppliesTo(values, setFieldTouched)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  label="Categories"
                                  required
                                  error={Boolean(getCurrentError(values, 'category_ids', '()', errors, touched))}
                                  helperText={getCurrentError(values, 'category_ids', '()', errors, touched)}
                                />
                              )}
                              renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'categories' })}
                            />
                          }
                        </>
                        }
                        <FormControlLabel
                          value="Specific Listings"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'sku' && cond.operator === '()'
                              )) || false}
                              onClick={() => onClickSpecificListingsAppliesTo(values, setFieldValue)}
                            />
                          }
                          label="Specific Listings"
                        />
                        {values.action_condition.conditions.find((cond) => (
                          cond.attribute_name === 'sku' && cond.operator === '()'
                        ))  &&
                          <>
                            {!catalogProducts.length ?
                              <div className="my-5">
                                <span className="text-muted">Preparing Listings Data...</span>
                                <LinearProgress />
                              </div>
                              :
                              <Autocomplete
                                multiple
                                fullWidth
                                disableClearable
                                noOptionsText={
                                  <Link
                                    to="/request-sku" target="_blank" onMouseDown={() => {
                                      window.open('/request-sku', '_blank');
                                    }}
                                  >
                                    Request Listings
                                  </Link>
                                }
                                size="small"
                                className="my-5 max-w-600"
                                defaultValue={findProducts(values, '()')}
                                options={catalogProducts}
                                getOptionLabel={(option) => {
                                  return option.name;
                                }}
                                onChange={(_, value, action, option) => changeSpecificListingsAppliesTo(value, action, option, values, setFieldValue)}
                                onBlur={() => onBlurSpecificListingsAppliesTo(values, setFieldTouched)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Listings"
                                    required
                                    error={Boolean(getCurrentError(values, 'sku', '()', errors, touched))}
                                    helperText={getCurrentError(values, 'sku', '()', errors, touched)}
                                  />
                                )}
                                renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                              />}
                          </>
                        }
                      </RadioGroup>
                    </FormControl>
                    <Grid item xs={12}>
                      <Typography variant="h5" className="font-bold mt-5">
                        Does Not Apply To:
                      </Typography>
                    </Grid>
                    <FormControl component="fieldset" className={`${classes.rootControl} w-full`}>
                      <RadioGroup
                        aria-label="does not apply to"
                      >
                        <FormControlLabel
                          value="None"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={Boolean(!values.action_condition.conditions.filter((cond) => cond.operator === '!()').length)}
                              onClick={() => {
                                const conditionsWithoutExcludes = values.action_condition.conditions.filter((cond) => cond.operator !== '!()');
                                setFieldValue('action_condition.conditions', conditionsWithoutExcludes);
                              }}
                            />
                          }
                          label="None"
                        />
                        <FormControlLabel
                          value="Specific Categories"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'category_ids' && cond.operator === '!()'
                              )) || false}
                              onClick={() => onClickSpecificCategoriesNotApplyTo(values, setFieldValue)}
                            />
                          }
                          label="Specific Categories"
                        />
                        {values.action_condition.conditions.find((cond) => (
                          cond.attribute_name === 'category_ids' && cond.operator === '!()'
                        )) &&
                        <>
                          {loadingCategories ?
                            <div className="my-5">
                              <span className="text-muted">Preparing Categories Data...</span>
                              <LinearProgress />
                            </div>
                            :
                            <Autocomplete
                              multiple
                              fullWidth
                              disableClearable
                              size="small"
                              className="my-5 max-w-600"
                              defaultValue={findCategories(values, '!()')}
                              options={storeActiveCategoriesWithActiveNavLevel}
                              getOptionLabel={(option) => option.nameWithPath}
                              onChange={(_, value, action, option) => changeSpecificCategoriesNotApplyTo(value, action, option, values, setFieldValue)}
                              onBlur={() => onBlurSpecificCategoriesNotApplyTo(values, setFieldTouched)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  label="Categories"
                                  required
                                  error={Boolean(getCurrentError(values, 'category_ids', '!()', errors, touched))}
                                  helperText={getCurrentError(values, 'category_ids', '!()', errors, touched)}
                                />
                              )}
                              renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'categories' })}
                            />
                          }
                        </>
                        }
                        <FormControlLabel
                          value="Specific Listings"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'sku' && cond.operator === '!()'
                              )) || false}
                              onClick={() => onClickSpecificListingsNotApplyTo(values, setFieldValue)}
                            />
                          }
                          label="Specific Listings"
                        />
                        {values.action_condition.conditions.find((cond) => (
                          cond.attribute_name === 'sku' && cond.operator === '!()'
                        ))  &&
                          <>
                            {productsLoading ?
                              <div className="my-5">
                                <span className="text-muted">Preparing Listings Data...</span>
                                <LinearProgress />
                              </div>
                              :
                              <Autocomplete
                                multiple
                                fullWidth
                                disableClearable
                                noOptionsText={
                                  <Link
                                    to="/request-sku" target="_blank" onMouseDown={() => {
                                      window.open('/request-sku', '_blank');
                                    }}
                                  >
                                    Request Listings
                                  </Link>
                                }
                                size="small"
                                className="my-5 max-w-600"
                                defaultValue={findProducts(values, '!()')}
                                options={catalogProducts}
                                getOptionLabel={(option) => option.name}
                                onChange={(_, value, action, option) => changeSpecificListingsNotApplyTo(value, action, option, values, setFieldValue)}
                                onBlur={() => onBlurSpecificListingsNotApplyTo(values, setFieldTouched)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Listings"
                                    required
                                    error={Boolean(getCurrentError(values, 'sku', '!()', errors, touched))}
                                    helperText={getCurrentError(values, 'sku', '!()', errors, touched)}
                                  />
                                )}
                                renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                              />}
                          </>
                        }
                      </RadioGroup>
                    </FormControl>
                  </>
                }
                <Grid item xs={12}>
                  <Typography variant="h5" className="font-bold mt-5">
                    Minimum Order to Qualify: <span className="text-error">*</span>
                  </Typography>
                </Grid>
                <div className="flex items-center mb-5 w-full">
                  <FormControl className={classes.rootControl} component="div" id="Min-order" fullWidth>
                    <RadioGroup
                      aria-label="Min-order"
                      name="Minimum order"
                      value={values.minOrder}
                      onChange={(event) => {
                        setFieldValue('minOrder', event.target.value);
                      }}
                    >
                      <FormControlLabel
                        value="None"
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            checked={Boolean(!values.action_condition.conditions.filter((cond) => cond.operator === '>=').length)}
                            onClick={() => {
                              const conditionsWithoutExcludes = values.action_condition.conditions.filter((cond) => cond.operator !== '>=');
                              setFieldValue('action_condition.conditions', conditionsWithoutExcludes);
                            }}
                          />
                        }
                        label="None"
                      />
                      <FormControlLabel
                        value="PurchaseAmount"
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            checked={!!values.action_condition.conditions.find((cond) => (
                              cond.attribute_name === 'quote_item_row_total' && cond.operator === '>='
                            )) || false}
                            onClick={() => {
                              const filteredConditions = values.action_condition.conditions.filter((cond) => (
                                cond.operator !== '>='
                              ));
                              setFieldValue('action_condition.conditions', [...filteredConditions, values.quote_item_row_total]);
                            }}
                          />
                        }
                        label={
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4} md={5} className="flex items-center">
                              Purchase Amount
                            </Grid>
                            {!!values.action_condition.conditions.find((cond) => (
                              cond.attribute_name === 'quote_item_row_total' && cond.operator === '>='
                            )) && (
                              <Grid item xs={12} sm={8} md={7}>
                                <NumberFormat
                                  customInput={TextField}
                                  prefix={'$'}
                                  required
                                  fullWidth
                                  value={values.action_condition.conditions.find((cond) => (cond.attribute_name === 'quote_item_row_total')).value}
                                  allowNegative={false}
                                  label="Amount"
                                  className="max-w-600"
                                  size="small"
                                  variant="outlined"
                                  name="minPurchaseAmount"
                                  onValueChange={(value) => changePurchaseAmount(value, values, setFieldValue)}
                                />
                              </Grid>
                            )}
                          </Grid>
                        }
                      />
                      <FormControlLabel
                        value="MinOrderQuontity"
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            checked={!!values.action_condition.conditions.find((cond) => (
                              cond.attribute_name === 'quote_item_qty' && cond.operator === '>='
                            )) || false}
                            onClick={() => {
                              const filteredConditions = values.action_condition.conditions.filter((cond) => (
                                cond.operator !== '>='
                              ));
                              setFieldValue('action_condition.conditions', [...filteredConditions, values.quote_item_qty]);
                            }}
                          />
                        }
                        label={
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4} md={3} className="flex items-center">
                              Quantity
                            </Grid>
                            {!!values.action_condition.conditions.find((cond) => (
                              cond.attribute_name === 'quote_item_qty' && cond.operator === '>='
                            )) &&
                              <Grid item xs={12} sm={8} md={9}>
                                <NumberFormat
                                  customInput={TextField}
                                  fullWidth
                                  required
                                  value={values.action_condition.conditions.find((cond) => (cond.attribute_name === 'quote_item_qty')).value}
                                  allowNegative={false}
                                  size="small"
                                  label="Quantity"
                                  className="max-w-600"
                                  variant="outlined"
                                  name="minOrderQuontity"
                                  onValueChange={(value) => changeMinOrderQuontity(value, values, setFieldValue)}
                                />
                              </Grid>
                            }
                          </Grid>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
                <Grid item xs={12}>
                  <Typography variant="h5" className="font-bold">
                    Customer Eligibility: <span className="text-error">*</span>
                  </Typography>
                </Grid>
                <FormControl component="div" className={`${classes.rootControl} w-full`}>
                  <RadioGroup
                    aria-label="applies to customers"
                  >
                    <FormControlLabel
                      value="Everyone"
                      control={
                        <Radio
                          size="small"
                          color="primary"
                          checked={values.customer_group_ids.length === allCustomerGroups.length}
                          onClick={() => setFieldValue('customer_group_ids', allCustomerGroups.map((g) => g.id))}
                        />}
                      label="Everyone"
                    />
                    <FormControlLabel
                      value="Specific group of customers"
                      control={
                        <Radio
                          size="small"
                          color="primary"
                          checked={values.customer_group_ids.length !== allCustomerGroups.length}
                          onClick={() => setFieldValue('customer_group_ids', [])}
                        />}
                      label="Specific group of customers"
                    />
                    {values.customer_group_ids.length !== allCustomerGroups.length &&
                    <Autocomplete
                      required
                      multiple
                      fullWidth
                      disableClearable
                      size="small"
                      className="max-w-600"
                      name="customer_group_ids"
                      defaultValue={selectedCustomerGroups}
                      options={allCustomerGroups}
                      getOptionLabel={(option) => option.code}
                      onChange={(event, value, action, option) => changeSpecificCustomersGroup(event, value, action, option, values, setFieldValue)}
                      onBlur={() => setFieldTouched('customer_group_ids', true)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          variant="outlined"
                          placeholder="Choose customer group"
                          error={touched.customer_group_ids && Boolean(errors.customer_group_ids)}
                          helperText={touched.customer_group_ids && errors.customer_group_ids}
                        />
                      )}
                    />
                    }
                  </RadioGroup>
                </FormControl>
                <Grid container spacing={2} className="m-1">
                  <Grid item xs={12} sm={4} md={3}>
                    <div className="flex-column ml-2">
                      <Typography variant="h5" className="mt-2">
                        Active Dates:
                      </Typography>
                      <div>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              name="allowEndDate"
                              color="primary"
                              checked={values.allowEndDate}
                              onChange={() => {
                                setFieldValue('allowEndDate', !values.allowEndDate);
                                setFieldValue('to_date', !values.allowEndDate ? format(new Date(), 'yyyy-MM-dd') : '');
                              }}
                            />
                          }
                          label="Set an End Date"
                        />
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={8} md={9} className="mt-3">
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <DatePicker
                        autoOk
                        variant="inline"
                        inputVariant="outlined"
                        format="MM/dd/yyyy"
                        emptyLabel="mm/dd/yyyy"
                        name="from_date"
                        className="ml-6 mr-2"
                        label="Start Date"
                        value={values.from_date && isValid(parseISO(values.from_date)) ? parseISO(values.from_date) : null}
                        onChange={(date) => {
                          setFieldValue('from_date', format(date, 'yyyy-MM-dd'));
                        }}
                        onBlur={() => setFieldTouched('from_date', true)}
                        error={touched.from_date && Boolean(errors.from_date)}
                        helperText={touched.from_date && errors.from_date}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <CalendarTodayIcon className="text-muted text-16" />
                          ),
                          endAdornment: (
                            <Icon
                              className="cursor-pointer"
                              onClick={(event) => {
                                event.stopPropagation();
                                setFieldValue('from_date', '');
                              }
                              }
                            >
                              close
                            </Icon>
                          )
                        }}
                      />
                      {values.allowEndDate &&
                        <DatePicker
                          autoOk
                          variant="inline"
                          format="MM/dd/yyyy"
                          emptyLabel="mm/dd/yyyy"
                          name="to_date"
                          label="End Date"
                          value={values.to_date && isValid(parseISO(values.to_date)) ? parseISO(values.to_date) : null}
                          onChange={(date) => {
                            setFieldValue('to_date', format(date, 'yyyy-MM-dd'));
                          }}
                          onBlur={() => setFieldTouched('to_date', true)}
                          inputVariant="outlined"
                          error={touched.to_date && Boolean(errors.to_date)}
                          helperText={touched.to_date && errors.to_date}
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <CalendarTodayIcon className="text-muted text-16" />
                            ),
                            endAdornment: (
                              <Icon
                                className="cursor-pointer"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setFieldValue('to_date', '');
                                  setFieldValue('allowEndDate', false);
                                }
                                }
                              >
                                close
                              </Icon>
                            )
                          }}
                        />
                      }
                    </MuiPickersUtilsProvider>
                  </Grid>
                </Grid>
                <Grid item xs={12} className="pt-7">
                  <Button
                    color="primary"
                    variant="contained"
                    className="mr-4"
                    type="submit"
                    disabled={isCreatedByMagento}
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setOpenEditRuleDialog(false)}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>
      }
      <BackdropCircular loading={loadingUpdateCouponRule} />
    </div>
  );
};

export default EditCouponRule;
